package com.evoapps.evoshift.domain.model

/**
 * Keys of [Notification.data]. Notifications store an English title and message (push text), and
 * these facts, so the app can show them in the reader's language and open the right screen.
 */
object NotificationData {
    const val SHIFT_ID = "shiftId"
    const val WORKPLACE_ID = "workplaceId"
    const val ROSTER_ID = "rosterId"
    const val ABSENCE_ID = "absenceId"
    /** SH-223: a remote-work, work-event or overtime request (work_requests), sent as an ABSENCE_REQUEST kind. */
    const val WORK_REQUEST_ID = "workRequestId"
    const val DATE = "date"
    const val START = "start"
    const val END = "end"
    const val NAME = "name"
    const val NOTE = "note"
    const val COUNT = "count"
    const val MONTH = "month"
    const val YEAR = "year"
    const val WORKPLACE_NAME = "workplaceName"
    /** The post a notification is about (SH-118, functions/announcements.js). */
    const val ANNOUNCEMENT_ID = "announcementId"
    /** The role a post went to (SH-118); none: everyone. */
    const val ROLE = "role"
    /**
     * The person a notification is about (e.g. who asked for an absence or a swap), so deleting
     * their account can remove the name from colleagues' inboxes (functions accountDeletion.js).
     * Never sent in a push.
     */
    const val SUBJECT_USER_ID = "subjectUserId"
    /** The job post a notification is about (SH-125, functions/hiring.js). */
    const val JOB_POST_ID = "jobPostId"
    /** The applicant a notification is about (SH-125). */
    const val APPLICATION_ID = "applicationId"
    /** The checklist's name (SH-121). */
    const val CHECKLIST = "checklist"
    /** The course a notification is about (SH-126, functions/courses.js), and its title. */
    const val COURSE_ID = "courseId"
    const val COURSE = "course"
    /** Distinguishes notifications of the same type, see [Kind]. */
    const val KIND = "kind"

    object Kind {
        /**
         * SH-236: a manager split a colleague's shift and the second part is yours from the
         * handover ([NAME]: the colleague). Type SHIFT_ASSIGNED, so older apps show "new shift".
         */
        const val TAKEOVER = "TAKEOVER"
        /**
         * SH-271: a republished month: the person's shifts changed since the last publish. Type
         * ROSTER_PUBLISHED, so older apps show "roster published".
         */
        const val ROSTER_UPDATED = "ROSTER_UPDATED"
        /** A colleague put a shift on the marketplace. */
        const val MARKETPLACE = "MARKETPLACE"
        /** A colleague asks you personally to take their shift. */
        const val DIRECT = "DIRECT"
        /** A manager has a swap request to review. */
        const val REVIEW = "REVIEW"
        /** The marketplace offer was approved by a manager (now public). */
        const val NOW_PUBLIC = "NOW_PUBLIC"
        const val ABSENCE_REQUEST = "ABSENCE_REQUEST"
        /** SH-223: a manager filed (and maybe approved) a request on the person's behalf; [NAME] is the manager. */
        const val REQUEST_ON_BEHALF = "REQUEST_ON_BEHALF"
        /** Someone has not clocked in (functions/noShow.js, SH-73). */
        const val NO_SHOW = "NO_SHOW"
        /** A closing checklist is not done, to the managers (functions/checklists.js, SH-121). */
        const val CHECKLIST_OVERDUE = "CHECKLIST_OVERDUE"
        /** The same, to the person who was on the shift. */
        const val CHECKLIST_REMINDER = "CHECKLIST_REMINDER"
        /** A manager reminds you to send your availability (SH-48). */
        const val AVAILABILITY_REMINDER = "AVAILABILITY_REMINDER"
        /** A manager posted to the team (SH-118); the text is the message, the author [NAME]. */
        const val ANNOUNCEMENT = "ANNOUNCEMENT"
        /** A manager left a note on your shift (SH-118); the note is [NOTE]. */
        const val SHIFT_NOTE = "SHIFT_NOTE"
        /**
         * SH-116: clocked in for the workplace's reminder hours without a break (functions/breaks.js);
         * [COUNT] is those hours. Type GENERAL, so older apps show the stored text.
         */
        const val BREAK_REMINDER = "BREAK_REMINDER"
        /**
         * SH-267: someone of the reader's team is planned at another workplace of the group that
         * month ([NAME] the person, [WORKPLACE_NAME] that workplace, [MONTH] and [YEAR]); once per
         * person, workplace and month (functions/borrowing.js). Type GENERAL.
         */
        const val BORROWED = "BORROWED"
        /** SH-126: a course to take ([COURSE], [COURSE_ID]; [DATE] its due day). Type GENERAL. */
        const val COURSE_ASSIGNED = "COURSE_ASSIGNED"
        /** SH-126: a manager recorded an OHS instruction the person confirms. Type GENERAL. */
        const val OHS_CONFIRMATION = "OHS_CONFIRMATION"
        /**
         * SH-125: someone applied to a job post (to the workplace's managers); the message is the
         * post's title, [JOB_POST_ID] and [APPLICATION_ID] open the applicant. No applicant data.
         */
        const val NEW_APPLICANT = "NEW_APPLICANT"
    }

    /** The shift's id, workplace, date and times, plus [extra] (null values are left out). */
    fun forShift(shift: Shift, workplaceId: String, vararg extra: Pair<String, String?>): Map<String, String> =
        buildMap {
            put(SHIFT_ID, shift.id)
            put(WORKPLACE_ID, workplaceId)
            put(DATE, shift.date)
            put(START, shift.plannedTime.start.substringAfter('T').take(5))
            put(END, shift.plannedTime.end.substringAfter('T').take(5))
            extra.forEach { (key, value) -> if (value != null) put(key, value) }
        }
}
